import React, { useState } from 'react';
import { Save, Trash2, X, AlertTriangle, Check, Loader2 } from 'lucide-react';
import { PackZoneAdmin, UpdatePackZoneAdminRequest } from '../../../../types/packZone';
import { packZoneService } from '../../../../services/packZoneService';
import { PrinterSelect } from '../../../common/PrinterSelect';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface PackZoneBulkUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedIds: string[];
  selectedData: PackZoneAdmin[];
  onUpdateSuccess: () => void;
  onDeleteSuccess: () => void;
}

interface UpdateFields {
  packZoneDescription: boolean;
  packageSlipPrinterName: boolean;
  shippingLabelsPrinterName: boolean;
  consignmentNotePrinterName: boolean;
  printerID: boolean;
  packageSlipNumOfCopies: boolean;
  labelExportFolder: boolean;
}

export const PackZoneBulkUpdateModal: React.FC<PackZoneBulkUpdateModalProps> = ({
  isOpen,
  onClose,
  selectedIds,
  selectedData,
  onUpdateSuccess,
  onDeleteSuccess
}) => {
  const { t } = useTranslation();
  const isSingleSelection = selectedIds.length === 1;
  const singleZone = isSingleSelection && selectedData.length === 1 ? selectedData[0] : null;

  const [updateFields, setUpdateFields] = useState<UpdateFields>({
    packZoneDescription: isSingleSelection,
    packageSlipPrinterName: isSingleSelection,
    shippingLabelsPrinterName: isSingleSelection,
    consignmentNotePrinterName: isSingleSelection,
    printerID: isSingleSelection,
    packageSlipNumOfCopies: isSingleSelection,
    labelExportFolder: isSingleSelection
  });

  const [packZoneDescription, setPackZoneDescription] = useState(singleZone?.packZoneDescription || '');
  const [packageSlipPrinterName, setPackageSlipPrinterName] = useState(singleZone?.packageSlipPrinterName || '');
  const [shippingLabelsPrinterName, setShippingLabelsPrinterName] = useState(singleZone?.shippingLabelsPrinterName || '');
  const [consignmentNotePrinterName, setConsignmentNotePrinterName] = useState(singleZone?.consignmentNotePrinterName || '');
  const [printerID, setPrinterID] = useState(singleZone?.printerID || '');
  const [packageSlipNumOfCopies, setPackageSlipNumOfCopies] = useState<number | undefined>(singleZone?.packageSlipNumOfCopies ?? undefined);
  const [labelExportFolder, setLabelExportFolder] = useState(singleZone?.labelExportFolder || '');

  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error' | 'warning'; message: string } | null>(null);

  if (!isOpen) return null;

  const hasSelectedFields = () => Object.values(updateFields).some(Boolean);

  const toggleField = (field: keyof UpdateFields) => {
    setUpdateFields(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleUpdate = async () => {
    if (!hasSelectedFields()) {
      setNotification({ type: 'error', message: t('manager.modules.adminDelivery.packZone.errors.selectAtLeastOneField', {}, 'Du må velge minst ett felt som skal oppdateres') });
      return;
    }

    setIsUpdating(true);
    setNotification(null);

    try {
      let successCount = 0;
      let errorCount = 0;

      for (const id of selectedIds) {
        try {
          const request: UpdatePackZoneAdminRequest = {};
          if (updateFields.packZoneDescription) request.packZoneDescription = packZoneDescription;
          if (updateFields.packageSlipPrinterName) request.packageSlipPrinterName = packageSlipPrinterName;
          if (updateFields.shippingLabelsPrinterName) request.shippingLabelsPrinterName = shippingLabelsPrinterName;
          if (updateFields.consignmentNotePrinterName) request.consignmentNotePrinterName = consignmentNotePrinterName;
          if (updateFields.printerID) request.printerID = printerID;
          if (updateFields.packageSlipNumOfCopies) request.packageSlipNumOfCopies = packageSlipNumOfCopies;
          if (updateFields.labelExportFolder) request.labelExportFolder = labelExportFolder;

          await packZoneService.updatePackZone(id, request);
          successCount++;
        } catch {
          errorCount++;
        }
      }

      if (errorCount > 0) {
        setNotification({ type: 'warning', message: t('manager.modules.adminDelivery.packZone.updatePartial', { '0': successCount, '1': selectedIds.length, '2': errorCount }, `Oppdatert ${successCount} av ${selectedIds.length} pakkesoner. ${errorCount} feil.`) });
      } else {
        setNotification({ type: 'success', message: t('manager.modules.adminDelivery.packZone.updateSuccess', { '0': successCount }, `${successCount} pakkesone(r) oppdatert`) });
      }
      onUpdateSuccess();
    } catch (err) {
      setNotification({ type: 'error', message: err instanceof Error ? err.message : t('manager.modules.adminDelivery.packZone.errors.updateFailed', {}, 'Oppdatering feilet') });
    } finally {
      setIsUpdating(false);
    }
  };

  const executeDelete = async () => {
    setIsDeleting(true);
    setShowDeleteConfirm(false);
    setNotification(null);

    try {
      let successCount = 0;
      for (const id of selectedIds) {
        try {
          await packZoneService.deletePackZone(id);
          successCount++;
        } catch { /* skip */ }
      }
      setNotification({ type: 'success', message: t('manager.modules.adminDelivery.packZone.deleteSuccess', { '0': successCount }, `${successCount} pakkesone(r) slettet`) });
      onDeleteSuccess();
    } catch (err) {
      setNotification({ type: 'error', message: err instanceof Error ? err.message : t('manager.modules.adminDelivery.packZone.errors.deleteFailed', {}, 'Sletting feilet') });
    } finally {
      setIsDeleting(false);
    }
  };

  const inputClass = "w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500";

  const renderFieldRow = (
    field: keyof UpdateFields,
    label: string,
    input: React.ReactNode
  ) => (
    <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
      <input
        type="checkbox"
        checked={updateFields[field]}
        onChange={() => toggleField(field)}
        className="h-4 w-4 text-blue-600 border-gray-300 rounded flex-shrink-0 mt-1"
      />
      <div className="flex-1">
        <label className="text-sm font-medium text-gray-700 mb-1 block">{label}</label>
        {updateFields[field] ? input : (
          <span className="text-sm text-gray-400 italic">{t('manager.modules.adminDelivery.packZone.notSelectedForUpdate', {}, 'Ikke valgt for oppdatering')}</span>
        )}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col relative">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('manager.modules.adminDelivery.packZone.bulk.title', {}, 'Vedlikehold pakkesoner')}</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {t('manager.modules.adminDelivery.packZone.selectedCount', { '0': selectedIds.length }, `${selectedIds.length} pakkesone(r) valgt`)}
              {isSingleSelection && singleZone && (
                <span className="ml-1 font-medium">— {singleZone.packZoneID}</span>
              )}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Notification */}
        {notification && (
          <div className={`mx-4 mt-3 p-3 rounded-lg text-sm flex items-center gap-2 ${
            notification.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
            notification.type === 'warning' ? 'bg-yellow-50 text-yellow-800 border border-yellow-200' :
            'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {notification.type === 'success' ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
            {notification.message}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <p className="text-xs text-gray-500 mb-3">
            {t('manager.modules.adminDelivery.packZone.bulk.intro', {}, 'Huk av feltene du vil oppdatere. Kun avhukede felter blir endret.')}
          </p>

          {/* Beskrivelse */}
          {renderFieldRow('packZoneDescription', t('common.fields.description', {}, 'Beskrivelse'), (
            <input type="text" value={packZoneDescription} onChange={e => setPackZoneDescription(e.target.value)} className={inputClass} placeholder={t('common.fields.description', {}, 'Beskrivelse')} />
          ))}

          {/* Skrivere med PrinterSelect.
              Bruker valueField="id" for å lagre numerisk PrintNode-ID istedenfor navn —
              konsistent med SystemSetup og Profile. Eliminerer tvetydighet når samme
              printernavn finnes på flere PrintNode-computere. */}
          {renderFieldRow('packageSlipPrinterName', t('manager.modules.adminDelivery.packZone.fields.packageSlipPrinter', {}, 'Pakkseddel-skriver'), (
            <PrinterSelect
              value={packageSlipPrinterName || null}
              onChange={(v) => setPackageSlipPrinterName(v || '')}
              valueField="id"
              showTestButton={true}
              size="sm"
            />
          ))}

          {renderFieldRow('shippingLabelsPrinterName', t('manager.modules.adminDelivery.packZone.fields.shippingLabelsPrinter', {}, 'Fraktetikett-skriver'), (
            <PrinterSelect
              value={shippingLabelsPrinterName || null}
              onChange={(v) => setShippingLabelsPrinterName(v || '')}
              valueField="id"
              showTestButton={true}
              size="sm"
            />
          ))}

          {renderFieldRow('consignmentNotePrinterName', t('manager.modules.adminDelivery.packZone.fields.consignmentNotePrinter', {}, 'Fraktbrev-skriver'), (
            <PrinterSelect
              value={consignmentNotePrinterName || null}
              onChange={(v) => setConsignmentNotePrinterName(v || '')}
              valueField="id"
              showTestButton={true}
              size="sm"
            />
          ))}

          {/* PrinterID og øvrige felt */}
          {renderFieldRow('printerID', t('manager.modules.adminDelivery.packZone.fields.printerID', {}, 'PrinterID fileksport(transport)'), (
            <input type="text" value={printerID} onChange={e => setPrinterID(e.target.value)} className={inputClass} placeholder={t('manager.modules.adminDelivery.packZone.placeholders.printerID', {}, 'PrinterID')} />
          ))}

          {renderFieldRow('packageSlipNumOfCopies', t('manager.modules.adminDelivery.packZone.fields.packageSlipNumOfCopies', {}, 'Ant. kopier pakkseddel'), (
            <input type="number" value={packageSlipNumOfCopies ?? ''} onChange={e => setPackageSlipNumOfCopies(e.target.value ? parseInt(e.target.value) : undefined)} className={inputClass} min={0} />
          ))}

          {renderFieldRow('labelExportFolder', t('manager.modules.adminDelivery.packZone.fields.labelExportFolder', {}, 'Eksportmappe'), (
            <input type="text" value={labelExportFolder} onChange={e => setLabelExportFolder(e.target.value)} className={inputClass} placeholder={t('manager.modules.adminDelivery.packZone.placeholders.exportFolder', {}, 'F.eks. C:\\BizClickExport\\Labels')} />
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t bg-gray-50">
          <button
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isUpdating || isDeleting}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-md hover:bg-red-100 disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" />
            {t('manager.modules.adminDelivery.packZone.deleteWithCount', { '0': selectedIds.length }, `Slett (${selectedIds.length})`)}
          </button>

          <div className="flex items-center gap-3">
            <button onClick={onClose} disabled={isUpdating || isDeleting} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50">
              {t('common.buttons.close', {}, 'Lukk')}
            </button>
            <button
              onClick={handleUpdate}
              disabled={isUpdating || isDeleting || !hasSelectedFields()}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isUpdating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {isUpdating ? t('manager.modules.adminDelivery.packZone.updating', {}, 'Oppdaterer...') : t('manager.modules.adminDelivery.packZone.updateWithCount', { '0': selectedIds.length }, `Oppdater (${selectedIds.length})`)}
            </button>
          </div>
        </div>

        {/* Delete confirmation */}
        {showDeleteConfirm && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
            <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="w-6 h-6 text-red-500" />
                <h3 className="text-lg font-semibold text-gray-900">{t('manager.modules.adminDelivery.packZone.confirmDeleteTitle', {}, 'Bekreft sletting')}</h3>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                {t('manager.modules.adminDelivery.packZone.confirmDeleteMessage', { '0': selectedIds.length }, `Er du sikker på at du vil slette ${selectedIds.length} pakkesone(r)? Denne handlingen kan ikke angres.`)}
              </p>
              <div className="flex justify-end gap-3">
                <button onClick={() => setShowDeleteConfirm(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">{t('common.buttons.cancel', {}, 'Avbryt')}</button>
                <button
                  onClick={executeDelete}
                  disabled={isDeleting}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  {isDeleting ? t('manager.modules.adminDelivery.common.deleting', {}, 'Sletter...') : t('common.buttons.delete', {}, 'Slett')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
